// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { TextInput } from '@mantine/core';
import type { Coding } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { ICD10_SYSTEM, lookupIcd10Code, normalizeIcd10Code } from '../../utils/icd10';

interface DiagnosisCodeInputProps {
  disabled?: boolean;
  label: string;
  value: string | undefined;
  onResolve: (coding: Coding) => void;
}

export function DiagnosisCodeInput({
  disabled = false,
  label,
  value,
  onResolve,
}: DiagnosisCodeInputProps): JSX.Element {
  const medplum = useMedplum();
  const [inputValue, setInputValue] = useState(value ?? '');
  const [error, setError] = useState<string | undefined>();
  const lookupRequestRef = useRef(0);

  useEffect(() => {
    setInputValue(value ?? '');
  }, [value]);

  const resolve = async (): Promise<void> => {
    const normalized = normalizeIcd10Code(inputValue);
    if (!normalized) {
      setInputValue(value ?? '');
      setError(undefined);
      return;
    }

    const requestId = ++lookupRequestRef.current;
    try {
      const coding = await lookupIcd10Code(medplum, normalized);
      if (requestId !== lookupRequestRef.current) {
        return;
      }
      if (!coding) {
        setError('Enter a valid ICD-10-CM diagnosis code');
        return;
      }
      setError(undefined);
      onResolve({ ...coding, system: ICD10_SYSTEM, code: normalized });
    } catch {
      if (requestId !== lookupRequestRef.current) {
        return;
      }
      setError('Unable to validate diagnosis code');
    }
  };

  return (
    <TextInput
      error={error}
      label={label}
      value={inputValue}
      disabled={disabled}
      readOnly={disabled}
      onChange={(event) => {
        setInputValue(event.currentTarget.value);
        setError(undefined);
      }}
      onBlur={() => {
        resolve().catch(() => setError('Unable to validate diagnosis code'));
      }}
    />
  );
}
